import React, {FC, PropsWithChildren, useMemo} from 'react';
import styled from 'styled-components';

import {Renderer} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {AnalyticsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipExplicitCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipExplicitCondition';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {makeAnalyticsReportTypeUrl} from '../../../analytics/helpers/analyticsUrlHelpers';
import {WorkspaceGenericItem} from '../../../components/inboxes/workspaces/workspaceGenericItem';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {getUnsavedViewNavigationBlockerId} from '../../../store/panels/analytics/analyticsPanelSelectors';
import {
  getAnalyticsReportType,
  makeGetAnalyticsParameters,
} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {fetchVisibleMetricsForReport} from '../../../store/panels/analytics/report/analyticsPanelReportActions';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {maybeRenderFeaturePillAndTooltip} from '../../helpers/metricLabels';
import {AnalyticsFeatureStatusesEnum, AnalyticsReportsEnum} from '../../helpers/reportHelpers/reportTypes';
import {ANALYTICS_DEPRECATED_IN_2025_TOOLTIP_CONTENT} from '../reportHeader/titleRow/reportHeaderLegacyPillWithTooltip';

/*
 * Props.
 */

interface ReportNavigationLinkProps {
  reportType: AnalyticsReportsEnum;
  panelId: AnalyticsPanelId;

  iconName?: IconName;
  selectedIconName?: IconName;
  renderIcon?: Renderer; // Takes precedence over `iconName` and `selectedIconName`.

  isDisabled: boolean;
  featureStatus?: AnalyticsFeatureStatusesEnum;
}

/*
 * Styles.
 */

const StyledLinkWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

/*
 * Component.
 */

export const ReportNavigationLink: FC<PropsWithChildren<ReportNavigationLinkProps>> = ({
  reportType,
  panelId,
  iconName,
  selectedIconName,
  children,
  isDisabled,
  featureStatus,
  renderIcon,
}) => {
  const getPanelId = useMemo(() => () => panelId, [panelId]);

  const selectedReportType = useAppSelector(getAnalyticsReportType);
  const dispatch = useAppDispatch();

  const doesMatch = reportType === selectedReportType;
  const parameters = useAppSelector(makeGetAnalyticsParameters(getPanelId));
  const unsavedViewNavigationBlockerId = useAppSelector(getUnsavedViewNavigationBlockerId);

  const onReportTypeClick = async (clickedReportType: AnalyticsReportsEnum) => {
    // We should ignore blocking navigation for report type and dates changes
    // because they are not stored in views.
    const path = makeAnalyticsReportTypeUrl(clickedReportType, parameters);
    await dispatch(navigateToLinkAction(path, undefined, unsavedViewNavigationBlockerId));
    // Fetch the metric when the report type changes
    dispatch(fetchVisibleMetricsForReport());
  };

  return (
    <WorkspaceGenericItem
      onClick={() => !doesMatch && onReportTypeClick(reportType)}
      isSelected={!isDisabled && doesMatch}
      iconName={iconName}
      selectedIconName={selectedIconName}
      renderIcon={renderIcon}
      shouldAlphabetizeWorkspace={false}
      workspaceDragItem={undefined}
      isDisabled={isDisabled}
    >
      <StyledLinkWrapperDiv>
        {children}
        <TooltipCoordinator customMargin={4} render={() => ANALYTICS_DEPRECATED_IN_2025_TOOLTIP_CONTENT}>
          <TooltipExplicitCondition isEnabled={featureStatus === AnalyticsFeatureStatusesEnum.LEGACY} />
          {maybeRenderFeaturePillAndTooltip(featureStatus, undefined, VisualSizesEnum.SMALL)}
        </TooltipCoordinator>
      </StyledLinkWrapperDiv>
    </WorkspaceGenericItem>
  );
};
